import { useState } from "react";
import { Divider, Portal, styled, Typography } from "@mui/material";
import { Layer, Map, GeoJSON } from "leaflet";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { useNavigate } from "hooks/routerHooks";
import { useCreateStudyArea, useUpdateStudyArea } from "hooks/studyAreaHooks";
import { useMeasuringMode } from "hooks/measuringHooks";

import { ConfigParameters, MeasureConf, StudyArea, Uuid } from "types/types";
import { getGeojsonFeature } from "utils/GeomanUtils";
import { deserializeErrors } from "utils/ErrorUtils";

import { DrawStudyArea } from "components/draw/basePolygon/DrawStudyArea";
import { DrawPolygon } from "components/icons/DrawPolygon";
import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { EditStudyArea } from "components/draw/basePolygon/EditStudyArea";
import { DeleteStudyArea } from "components/draw/basePolygon/DeleteStudyArea";
import { DrawInitialPolygonFooter } from "components/footer/DrawInitialPolygonFooter";
import { RenderStudyArea } from "components/map/layers/RenderStudyArea";
import { RenderMeasureConfigs } from "components/measures/addMeasurePage/RenderMeasureConfigs";
import { UploadStudyArea } from "components/upload/UploadStudyArea";

interface Props {
  mapRef: Map;
  footerRef: HTMLDivElement;
  configParameters: ConfigParameters;
  studyArea?: StudyArea;
  measureConfigs: MeasureConf[];
  uuid?: string;
}

export function StudyAreaDrawerContent(props: Props) {
  const {
    mapRef,
    footerRef,
    configParameters,
    studyArea,
    measureConfigs,
    uuid,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const isReadOnly = !!studyArea?.readOnly;
  const hasMeasureConfigs = measureConfigs.length > 0;
  const { isMeasuring } = useMeasuringMode();
  const isDisabled = hasMeasureConfigs || isReadOnly;

  const { mutateAsync: create, isLoading } = useCreateStudyArea();
  const { mutateAsync: update, isLoading: isUpdating } = useUpdateStudyArea(
    uuid || ""
  );

  const [isEditing, setIsEditing] = useState(false);
  const [geoJSONLayer, setGeoJSONLayer] = useState<GeoJSON | Layer | null>(
    studyArea ? studyArea.geometry_4326 : null
  );

  async function createStudyArea(geoJSONLayer: GeoJSON | Layer) {
    const geojsonStudyArea = getGeojsonFeature(geoJSONLayer);
    create(geojsonStudyArea.geometry, {
      onSuccess: (uuid: Uuid) => {
        enqueueSnackbar(t("label.studyAreaSuccessfullyCreated"), {
          variant: "success",
        });
        navigate(`/${uuid.uuid}/climate-effects`);
      },
      onError: (error) => {
        const errors = deserializeErrors(error);
        errors.map((er) => {
          return enqueueSnackbar(er.message, {
            variant: "error",
          });
        });
      },
    });
  }

  function updateStudyArea(geoJSONLayer: GeoJSON | Layer, uuid: string) {
    const geojsonStudyArea = getGeojsonFeature(geoJSONLayer);
    update(geojsonStudyArea.geometry, {
      onSuccess: () => {
        enqueueSnackbar(t("label.studyAreaSuccessfullyUpdated"), {
          variant: "success",
        });
        navigate(`/${uuid}/climate-effects`);
      },
      onError: (error) => {
        const errors = deserializeErrors(error);
        errors.map((er) => {
          return enqueueSnackbar(er.message, {
            variant: "error",
          });
        });
      },
    });
  }

  function onFinishPolygon() {
    if (!geoJSONLayer) return;
    if (!uuid) return createStudyArea(geoJSONLayer);
    if (hasMeasureConfigs || isReadOnly) navigate(`/${uuid}/climate-effects`);
    return updateStudyArea(geoJSONLayer, uuid);
  }

  function addGeoJSONLayer(geoJSONLayer: Layer | null) {
    setGeoJSONLayer(geoJSONLayer);
  }

  function deleteGeoJSONLayer() {
    setGeoJSONLayer(null);
  }

  return (
    <StyledPaddedPaper padding={6}>
      <Typography variant="h1-no-bold" component="h1">
        {t("label.studyArea")}
      </Typography>
      {geoJSONLayer && (
        <RenderStudyArea mapRef={mapRef} studyAreaGeo={geoJSONLayer} />
      )}
      {!isMeasuring && !geoJSONLayer && (
        <DrawStudyArea
          geoJSONLayer={geoJSONLayer}
          mapRef={mapRef}
          color="purple"
          fillColor="transparent"
          addGeoJSONLayer={addGeoJSONLayer}
          configParameters={configParameters}
        />
      )}

      <Typography variant="subtitle1" component="p" className="text-with-icon">
        <DrawPolygon /> {t("label.drawOnTheMap")}
      </Typography>
      {geoJSONLayer && (
        <EditStudyArea
          layer={geoJSONLayer}
          isEditing={isEditing}
          isDisabled={isDisabled}
          configParameters={configParameters}
          addGeoJSONLayer={addGeoJSONLayer}
          setIsEditing={setIsEditing}
        />
      )}
      {geoJSONLayer && (
        <DeleteStudyArea
          layer={geoJSONLayer}
          isDisabled={isDisabled}
          setIsEditing={setIsEditing}
          deleteGeoJSONLayer={deleteGeoJSONLayer}
        />
      )}

      <Typography>
        {t("label.searchYourLocationOrByZoomingAndDrawStudyArea")}
      </Typography>
      <Divider>{t("label.or")}</Divider>
      <UploadStudyArea
        isDisabled={isDisabled}
        setGeoJSONLayer={setGeoJSONLayer}
        maxUploadSizeMB={configParameters.maxUploadSizeMB}
      />
      <Portal container={footerRef}>
        <DrawInitialPolygonFooter
          onFinishPolygon={onFinishPolygon}
          isLoading={isLoading || isUpdating}
          isPolygonFinished={!!geoJSONLayer && !isEditing}
        />
      </Portal>
      <RenderMeasureConfigs mapRef={mapRef} measureConfigs={measureConfigs} />
    </StyledPaddedPaper>
  );
}

const StyledPaddedPaper = styled(PaddedPaper)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  .text-with-icon {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    .MuiSvgIcon-root {
      font-size: ${({ theme }) => theme.spacing(5)};
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;
